import { Component, ChangeDetectionStrategy, inject, signal, computed, output, OnInit } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirRefresh, iconoirTrash } from '@ng-icons/iconoir';
import { VoiceSessionService } from '../../../services/voice-session.service';
import { VoiceSessionSummary } from '../../../models';

/** États du chargement de la liste d'historique */
type HistoryStatus = 'idle' | 'loading' | 'error' | 'loaded';

/** Unités décroissantes utilisées par `formatRelativeTime` (les plus grandes d'abord) */
const RELATIVE_TIME_UNITS: readonly { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 }
];

const relativeTimeFormatter = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });

/**
 * Formate une date ISO en date relative française ("il y a 3 jours",
 * "aujourd'hui", "dans 2 heures"...). Pas d'équivalent existant dans le repo.
 */
function formatRelativeTime(isoDate: string): string {
  const diffSeconds = Math.round((new Date(isoDate).getTime() - Date.now()) / 1000);

  if (Math.abs(diffSeconds) < 60) {
    return 'à l\'instant';
  }

  for (const { unit, seconds } of RELATIVE_TIME_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) {
      return relativeTimeFormatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }

  return relativeTimeFormatter.format(Math.round(diffSeconds / 60), 'minute');
}

/**
 * Titre affiché pour une session : son titre stocké, ou un fallback formaté
 * sur sa date de création ("Conversation du 22/09/2026") si absent.
 */
function formatSessionTitle(session: VoiceSessionSummary): string {
  if (session.title) return session.title;

  const date = new Date(session.createdAt).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return `Conversation du ${date}`;
}

/**
 * Panneau d'historique des conversations vocales (MOKA-205, Phase 2).
 * Fetch au moment de l'ouverture (`ngOnInit`, le composant est monté/démonté
 * par le `@if` du parent), gère ses propres états vide/chargement/erreur, et
 * délègue à l'overlay la reprise de session / nouvelle conversation via ses
 * `output()` — il ne touche jamais lui-même à `VoiceSessionService.loadSession`
 * ou `startNewConversation`, pour garder la responsabilité de la navigation
 * de session dans l'overlay (cf. `VoiceAssistantOverlayComponent`).
 *
 * Suppression de session (MOKA-206, Lot D, RGPD) : confirmation inline par
 * item (pas de `MatDialog`), retrait optimiste de `#sessions` puis appel
 * réseau — en cas d'échec, l'item est restauré et un état d'erreur ciblé
 * s'affiche sur CET item (jamais un état d'erreur global qui écraserait
 * toute la liste déjà chargée). Si la session supprimée était la session
 * active, `activeSessionDeleted` est émis : c'est à l'overlay (qui connaît
 * `showHistory`) de décider de refermer le panneau — ce composant ne le fait
 * pas lui-même, même logique de séparation des responsabilités que pour
 * `sessionSelected`/`newConversation`.
 */
@Component({
  selector: 'app-voice-session-history',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon],
  viewProviders: [provideIcons({ iconoirRefresh, iconoirTrash })],
  templateUrl: './voice-session-history.component.html',
  styleUrl: './voice-session-history.component.scss'
})
export class VoiceSessionHistoryComponent implements OnInit {
  #voiceSession = inject(VoiceSessionService);

  sessionSelected = output<string>();
  newConversation = output<void>();
  /** Émis quand la session supprimée était la session active — à câbler côté `voice-assistant-overlay.component.ts` (hors scope de ce composant) pour refermer le panneau, cf. `onHistoryNewConversation()` existant */
  activeSessionDeleted = output<void>();

  #status = signal<HistoryStatus>('idle');
  status = computed(() => this.#status());

  #sessions = signal<VoiceSessionSummary[]>([]);
  sessions = computed(() => this.#sessions());

  /** Ids de sessions dont la confirmation de suppression inline est affichée */
  #confirmingIds = signal<ReadonlySet<string>>(new Set());

  /** Ids de sessions dont la dernière tentative de suppression a échoué (état d'erreur ciblé par item, jamais global) */
  #errorIds = signal<ReadonlySet<string>>(new Set());

  isLoading = computed(() => this.status() === 'loading');
  isError = computed(() => this.status() === 'error');
  isEmpty = computed(() => this.status() === 'loaded' && this.sessions().length === 0);
  hasSessions = computed(() => this.status() === 'loaded' && this.sessions().length > 0);

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.#status.set('loading');
    try {
      const sessions = await this.#voiceSession.fetchHistory();
      this.#sessions.set(sessions);
      this.#status.set('loaded');
    } catch (err) {
      console.warn('[VoiceSessionHistory] Échec du chargement de l\'historique', err);
      this.#status.set('error');
    }
  }

  formatTitle(session: VoiceSessionSummary): string {
    return formatSessionTitle(session);
  }

  formatRelative(session: VoiceSessionSummary): string {
    return formatRelativeTime(session.updatedAt);
  }

  onSelect(session: VoiceSessionSummary): void {
    this.sessionSelected.emit(session.id);
  }

  onNewConversation(): void {
    this.newConversation.emit();
  }

  onRetry(): void {
    this.load();
  }

  isConfirming(id: string): boolean {
    return this.#confirmingIds().has(id);
  }

  hasError(id: string): boolean {
    return this.#errorIds().has(id);
  }

  /**
   * Ouvre la confirmation inline pour cet item. Le bouton poubelle est un
   * bouton FRÈRE du bouton principal de sélection (pas imbriqué dedans — un
   * `<button>` dans un `<button>` est invalide en HTML), donc son clic ne
   * déclenche déjà pas `onSelect` ; `stopPropagation` reste un garde-fou
   * supplémentaire bon marché.
   */
  onRequestDelete(event: Event, session: VoiceSessionSummary): void {
    event.stopPropagation();
    this.#clearError(session.id);
    this.#confirmingIds.update(ids => new Set(ids).add(session.id));
  }

  onCancelDelete(event: Event, sessionId: string): void {
    event.stopPropagation();
    this.#closeConfirm(sessionId);
  }

  /**
   * Suppression confirmée : retrait optimiste immédiat de `#sessions`, puis
   * appel réseau via `VoiceSessionService.deleteSession`. En cas d'échec,
   * l'item est restauré à SA POSITION D'ORIGINE (moins déstabilisant pour
   * l'oeil qu'une réapparition en tête de liste) et un état d'erreur ciblé
   * sur cet item s'affiche — jamais un état d'erreur global qui écraserait
   * toute la liste déjà chargée.
   * Si la session supprimée était la session active, `VoiceSessionService`
   * repart déjà sur une conversation neuve (son propre `reset()` interne) ;
   * `activeSessionDeleted` est émis en plus pour que l'overlay puisse
   * refermer le panneau d'historique.
   */
  async onConfirmDelete(event: Event, session: VoiceSessionSummary): Promise<void> {
    event.stopPropagation();
    const sessionId = session.id;
    const originalIndex = this.#sessions().findIndex(s => s.id === sessionId);
    const wasActive = this.#voiceSession.persistedSessionId() === sessionId;

    this.#closeConfirm(sessionId);
    this.#sessions.update(list => list.filter(s => s.id !== sessionId));

    try {
      await this.#voiceSession.deleteSession(sessionId);
      if (wasActive) {
        this.activeSessionDeleted.emit();
      }
    } catch (err) {
      console.warn('[VoiceSessionHistory] Échec de la suppression de la session', err);
      this.#sessions.update(list => {
        if (list.some(s => s.id === sessionId)) return list;
        const copy = [...list];
        const insertAt = originalIndex >= 0 && originalIndex <= copy.length ? originalIndex : copy.length;
        copy.splice(insertAt, 0, session);
        return copy;
      });
      this.#errorIds.update(ids => new Set(ids).add(sessionId));
    }
  }

  #closeConfirm(sessionId: string): void {
    this.#confirmingIds.update(ids => {
      if (!ids.has(sessionId)) return ids;
      const next = new Set(ids);
      next.delete(sessionId);
      return next;
    });
  }

  #clearError(sessionId: string): void {
    this.#errorIds.update(ids => {
      if (!ids.has(sessionId)) return ids;
      const next = new Set(ids);
      next.delete(sessionId);
      return next;
    });
  }
}
